import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatPrice } from '../../../utils/formatters';
import InlineBanner from './InlineBanner';
import ComponentCard from './ComponentCard';
import { supabase } from '../../../lib/supabase';

const ComponentListView = ({ components, onAddToCart, isAuthenticated, onAuthRequired, onViewDetails, onAddToWishlist, onRemoveFromCollection, collectionItems, inlineBanner, columns, isStorefront = false, cardLayout = null }) => {
  const navigate = useNavigate();
  
  
  
                const formatSeoPath = (name, categoryName) => {
    if (!name) return '';
    const safeCat = (categoryName || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    let prefix = `/product`;
    if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
    
    let processedName = name.toLowerCase();
    let year = '';
    if (prefix === '/vehicle') {
      const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
      if (yearMatch) {
        year = `/${yearMatch[1]}`;
        processedName = processedName.replace(yearMatch[0], '').trim();
      }
    }
    
    const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
    return `${prefix}${year}/${pathSegments}`;
  };

  const handleViewIn3D = (component) => {
    // Symmetrical Component Views Increments:
    // Increments views on commercial seller offering and parent global components dynamically!
    if (component?.id && component.id.length === 36 && component.id.includes('-')) {
      supabase?.rpc('increment_seller_component_views', { seller_component_id: component.id })?.then(undefined, err => {
        console.error('Error incrementing seller component views:', err);
      });
    }
    const globalCompId = component.dbComponentId || component.componentId || component.component_id || component.component?.id;
    if (globalCompId) {
      supabase?.rpc('increment_component_views', { component_id: globalCompId })?.then(undefined, err => {
        console.error('Error incrementing global component views:', err);
      });
    }

    const pId = component.productId || component.product?.id;
    if (pId) {
      const pName = component.product?.name;
      const cSlug = component.slug;
      
      let url = `/3d-model-viewer?id=${pId}&componentId=${component.id}`;
      
      if (pName) {
        url = formatSeoPath(pName, component.product?.categoryName || component.product?.category?.name);
        if (cSlug) {
            url += `/${cSlug}`;
        }
        url += `/parts-diagram?componentId=${component.id}&id=${pId}`;
      }
      
      // Add view mode params
      url += (url.includes('?') ? '&' : '?') + `viewMode=x-ray-gray&isPlaying=false`;
      
      navigate(url);
    } else {
      console.warn("Component does not have a productId associated.");
      alert("This component is not linked to a 3D model.");
    }
  };

  const handleFavoriteClick = async (e, component) => {
    e.stopPropagation();
    if (!isAuthenticated) {
        onAuthRequired();
        return;
    }
    const isSaved = collectionItems?.includes(component.id);
    if (isSaved) {
        await onRemoveFromCollection(component.id);
    } else {
        await onAddToWishlist(component.id);
    }
  };

  const handleViewRatings = (e, component) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      onAuthRequired('Please log in to view ratings and reviews.');
      return;
    }
    const pId = component.productId || component.product?.id;
    if (pId) {
      navigate(`/product-reviews-ratings?productId=${pId}&componentId=${component.id}`);
    } else {
      alert("This component is not linked to a 3D model with reviews.");
    }
  };

  const itemsToRender = [...(components || [])];
  const colCount = Math.max(1, columns || 1);
  if (inlineBanner && itemsToRender.length >= colCount) {
    const availableCompleteRows = Math.floor(itemsToRender.length / colCount);
    const rowsBeforeBanner = Math.min(3, Math.max(1, availableCompleteRows));
    const insertIdx = rowsBeforeBanner * colCount;
    itemsToRender.splice(insertIdx, 0, { isBanner: true, ...inlineBanner });
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
      {itemsToRender?.map((component, index) => {
        if (component.isBanner) {
            return <div key={`banner-${index}`} className="col-span-full w-full"><InlineBanner banner={component} isAuthenticated={isAuthenticated} onAuthRequired={onAuthRequired} /></div>;
        }

        const isSaved = collectionItems?.includes(component?.id);

        return (
          <ComponentCard
            key={component?.id}
            component={component}
            onAddToCart={onAddToCart}
            isAuthenticated={isAuthenticated}
            onAuthRequired={onAuthRequired}
            onViewDetails={onViewDetails}
            onAddToWishlist={onAddToWishlist}
            onRemoveFromCollection={onRemoveFromCollection}
            collectionItems={collectionItems}
            isStorefront={isStorefront}
            cardLayout={cardLayout}
          />
        );
      })}
    </div>
  );
};

export default ComponentListView;